<template>
  <b-card class="card white-container">
    <multiselect
            class="currency-select"
            :searchable="false"
            v-if="isCurrency"
            v-model="curValue"
            @input="value => {createParamsObject('currency', value)}"
            :options="selectOptions"
            :preselect-first="true"
            :allow-empty="false"
            select-label=""
            selected-label=""
            deselect-label=""
    >
      <template slot="noResult">
        {{$t('global.no_results')}}
      </template>
    </multiselect>
    <div class="card-info">
      <div class="title-row">
      <span class="title-row__title">
        {{ title }}
      </span>
        <custom-tooltip
                class="title-row__tooltip"
                v-if="this.tooltipName && this.tooltipText"
                :name="this.tooltipName"
        >
          {{ $t(this.tooltipText) }}
        </custom-tooltip>
        <div v-if="percent !== false" :style="percentColor" class="title-row__percent">
          {{ percent }}%
        </div>
      </div>
      <div class="total-row">
        <div class="total-row__current">{{ this.getCurrentValue() }}</div>
        <div class="total-row__previous" v-if="this.getCompareValue()" >{{ this.getCompareValue() }}</div>
      </div>
      <compare-chart-line
              :chart-id="tooltipName"
              :is-currency="isCurrency"
              :data="chartLineData"
              :item-value-decorator="itemValueDecorator"
              :multiplier="getMultiplier()"
      />
    </div>
  </b-card>
</template>

<script>
import CompareChartLine from './CompareChartLine';
import _ from 'lodash';

export default {
  name: 'StatisticsCard',
  components: {
    CompareChartLine,
  },
  props: {
    labels: {
      type: Array,
      default: null,
    },
    data: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    tooltipName: {
      type: String,
      default: '',
    },
    tooltipText: {
      type: String,
      default: '',
    },
    isCurrency: {
      type: Boolean,
      default: true,
    },
    itemValueDecorator: {
      type: Function,
      default: null,
    },
    currentValueDecorator: {
      type: Function,
      default: null,
    },
    comparedValueDecorator: {
      type: Function,
      default: null,
    },
    groupBy: {
      type: String,
      default: 'day',
    },
  },
  data() {
    return {
      PERCENT_COLORS: {
        LOW: 'red',
        ZERO: '#656777',
        HIGH: '#04b82b',
      },
      defaultCurrencyVal: 'usd',
      curValue: 'usd',
      defaultCurrencies: ['usd'],
    };
  },
  computed: {
    percent() {
      if (this.data && this.data.change) return this.data.change;
      return false;
    },
    percentColor() {
      const percent = this.percent;
      let selectedColor = this.PERCENT_COLORS.ZERO;
      if (percent < 0) {
        selectedColor = this.PERCENT_COLORS.LOW;
      }
      if (percent > 0) {
        selectedColor = this.PERCENT_COLORS.HIGH;
      }
      return { color: `${selectedColor}`, ['border-color']: `${selectedColor}` };
    },
    chartLineData () {
      if ( !this.isCurrency ) {
        return this.data
      }
      return _.get(this.data, this.curValue, {});
    },
    selectOptions() {
      if( !this.isCurrency ) {
        return this.defaultCurrencies;
      }

      return !_.isEmpty(this.chartCurrencies) ? this.chartCurrencies : this.defaultCurrencies;
    },
    chartCurrencies() {
      if( !this.isCurrency ) {
        return this.defaultCurrencies;
      }

      let currencies = _.filter(
              _.keys(this.data),
              (currency) => ( this.getCurrencies().includes(currency) )
      );

      return !_.isEmpty(currencies) ? currencies : this.defaultCurrencies;
    },
  },
  methods: {
    click() {
      this.percent++;
    },
    getCurrentValue() {
      let value = _.get(this.data, `current_value`, 0);

      if (this.isCurrency) {
        value = _.get(this.data, `${this.curValue}.current_value`, 0);
      }

      if (this.currentValueDecorator) {
        value = this.currentValueDecorator(value, this.getMultiplier());
      }

      if (this.isCurrency) {
        value = `${this.getCurrencySign(_.upperCase(this.curValue))} ${this.formatAmount(value)}`;
      } else {
        value = value.toLocaleString();
      }

      return value;
    },
    getCompareValue() {
      if (!_.has(this.data, 'compare_value')) {
        return null;
      }

      let value = _.get(this.data, 'compare_value', 0);

      if (this.comparedValueDecorator) {
        console.log('getCompareValue', value, this.getMultiplier());
        value = this.comparedValueDecorator(value, this.getMultiplier());
      }

      if (this.isCurrency) {
        value = `${this.getCurrencySign(_.upperCase(this.curValue))} ${this.formatAmount(value)}`;
      } else {
        value = value.toLocaleString();
      }

      return `${value} previous period`;
    },
    getMultiplier() {
      switch (this.groupBy) {
        case 'day':
          return 30;
        case 'week':
          return 4;
        case 'month':
          return 1;
        case 'year':
          return (1 / 12);
        default:
          return 1;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.card {
  padding: 0;
}
.title-row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  &__title {
    font-size: 22px;
    font-weight: 500;
    color: #23282C;
  }

  &__tooltip {
    margin: 0 6px;
  }

  &__percent {
    border-radius: 4px;
    border: 1px solid;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 600;
    line-height: 10px;
  }
}

.total-row {
  display: flex;
  align-items: center;
  margin-bottom: 7px;

  &__current {
    font-size: 14px;
    margin-right: 8px;
    color: #743b91;
    font-weight: 600;
  }

  &__previous {
    color: #656777;
    font-size: 13px;
  }
}
</style>
